import React from "react";
import { Tooltip } from "antd";

// Chart configuration constants
const CHART_HEIGHT = 200;
const LABEL_HEIGHT = 32;
const DAYS_TO_SHOW = 14;

export interface VolumeData {
  date: string;
  received: number;
  approved: number;
  denied: number;
  canceled: number;
}

interface VolumeTrendChartProps {
  data: VolumeData[];
  onDrilldown?: (filterKey: string, title: string) => void;
}

export const VolumeTrendLegend: React.FC = () => (
  <div className="chart-legend-compact">
    <span className="legend-item">
      <span className="legend-color legend-submitted"></span> Received
    </span>
    <span className="legend-item">
      <span className="legend-color legend-decided"></span> Approved
    </span>
    <span className="legend-item">
      <span
        className="legend-color"
        style={{ backgroundColor: "#ff4d4f" }}
      ></span>{" "}
      Denied
    </span>
    <span className="legend-item">
      <span
        className="legend-color"
        style={{ backgroundColor: "#bac9c7" }}
      ></span>{" "}
      Canceled
    </span>
  </div>
);

export const VolumeTrendChart: React.FC<VolumeTrendChartProps> = ({
  data,
  onDrilldown,
}) => {
  // Show last N days
  const lastNDays = data.slice(-DAYS_TO_SHOW);
  const maxValue = Math.max(
    ...lastNDays.map((d) =>
      Math.max(d.received, d.approved, d.denied, d.canceled)
    )
  );

  // Helper to format date as YYYYMMDD
  const formatDateKey = (dateStr: string): string => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${month}${day}`;
  };

  return (
    <div className="line-chart" style={{ height: "auto" }}>
      <div
        className="line-chart-canvas"
        style={{
          height: `${CHART_HEIGHT + LABEL_HEIGHT}px`,
          paddingTop: "16px",
        }}
      >
        {lastNDays.map((item, idx) => {
          const receivedHeight = (item.received / maxValue) * CHART_HEIGHT;
          const approvedHeight = (item.approved / maxValue) * CHART_HEIGHT;
          const deniedHeight = (item.denied / maxValue) * CHART_HEIGHT;
          const canceledHeight = (item.canceled / maxValue) * CHART_HEIGHT;
          const date = new Date(item.date);
          const dateLabel = `${date.getMonth() + 1}/${date.getDate()}`;

          // Calculate percentages
          const approvedPercent =
            item.received > 0 ? (item.approved / item.received) * 100 : 0;
          const deniedPercent =
            item.received > 0 ? (item.denied / item.received) * 100 : 0;
          const canceledPercent =
            item.received > 0 ? (item.canceled / item.received) * 100 : 0;

          const dateKey = formatDateKey(item.date);

          return (
            <Tooltip
              key={idx}
              title={
                <div>
                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                    {dateLabel}
                  </div>
                  <div>Received: {item.received}</div>
                  <div>
                    Approved: {item.approved}{" "}
                    <span style={{ fontSize: "0.85em" }}>
                      ({Math.round(approvedPercent)}%)
                    </span>
                  </div>
                  <div>
                    Denied: {item.denied}{" "}
                    <span style={{ fontSize: "0.85em" }}>
                      ({Math.round(deniedPercent)}%)
                    </span>
                  </div>
                  <div>
                    Canceled: {item.canceled}{" "}
                    <span style={{ fontSize: "0.85em" }}>
                      ({Math.round(canceledPercent)}%)
                    </span>
                  </div>
                </div>
              }
              placement="top"
            >
              <div
                className="line-chart-bar"
                style={{
                  cursor: "default",
                  height: "100%",
                }}
              >
                <div
                  className="line-chart-col"
                  style={{ height: `${CHART_HEIGHT}px`, flex: "0 0 auto" }}
                >
                  <div
                    className="line-bar line-bar-submitted"
                    style={{
                      height: `${receivedHeight}px`,
                      cursor: onDrilldown ? "pointer" : "default",
                    }}
                    onClick={(e) => {
                      if (onDrilldown) {
                        e.stopPropagation();
                        onDrilldown(
                          `received__${dateKey}`,
                          `${dateLabel} Applications`
                        );
                      }
                    }}
                  />
                  <div
                    className="line-bar line-bar-decided"
                    style={{
                      height: `${approvedHeight}px`,
                      cursor: onDrilldown ? "pointer" : "default",
                    }}
                    onClick={(e) => {
                      if (onDrilldown) {
                        e.stopPropagation();
                        onDrilldown(
                          `approved__${dateKey}`,
                          `${dateLabel} Applications (Approved)`
                        );
                      }
                    }}
                  />
                  <div
                    className="line-bar"
                    style={{
                      height: `${deniedHeight}px`,
                      backgroundColor: "#ff4d4f",
                      cursor: onDrilldown ? "pointer" : "default",
                    }}
                    onClick={(e) => {
                      if (onDrilldown) {
                        e.stopPropagation();
                        onDrilldown(
                          `denied__${dateKey}`,
                          `${dateLabel} Applications (Denied)`
                        );
                      }
                    }}
                  />
                  <div
                    className="line-bar"
                    style={{
                      height: `${canceledHeight}px`,
                      backgroundColor: "#bac9c7",
                      cursor: onDrilldown ? "pointer" : "default",
                    }}
                    onClick={(e) => {
                      if (onDrilldown) {
                        e.stopPropagation();
                        onDrilldown(
                          `canceled__${dateKey}`,
                          `${dateLabel} Applications (Canceled)`
                        );
                      }
                    }}
                  />
                </div>
                <div className="line-chart-label">
                  {new Date(item.date).getDate()}
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};
